import type { ParkingLot } from '../models/ParkingLot';
import { TILE, type TileType } from '../models/Tile';

/**
 * Grid pathfinding for vehicle movement (spec section 9).
 *
 * Pure functions over the parking-lot grid. "Navigable" tiles are the ones a
 * car can drive on (roads + gates). Parking spaces are reached by driving to an
 * adjacent navigable tile, then pulling in. BFS is enough on a small uniform
 * grid and keeps results deterministic.
 */

export const NAVIGABLE: ReadonlySet<TileType> = new Set<TileType>([
  TILE.Road,
  TILE.Entrance,
  TILE.Exit,
]);

const NEIGHBORS: ReadonlyArray<[number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

function idx(width: number, x: number, y: number): number {
  return y * width + x;
}

export function isNavigable(lot: ParkingLot, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= lot.width || y >= lot.height) return false;
  return NAVIGABLE.has(lot.tiles[idx(lot.width, x, y)]);
}

/**
 * Breadth-first predecessor map from one or more start tiles over navigable
 * tiles. Returns dist (game-tiles) and prev (tile index -> previous index).
 */
export function bfsFrom(
  lot: ParkingLot,
  starts: number[]
): { dist: Map<number, number>; prev: Map<number, number> } {
  const dist = new Map<number, number>();
  const prev = new Map<number, number>();
  const queue: number[] = [];

  for (const s of starts) {
    if (dist.has(s)) continue;
    dist.set(s, 0);
    queue.push(s);
  }

  let head = 0;
  while (head < queue.length) {
    const current = queue[head++];
    const cx = current % lot.width;
    const cy = Math.floor(current / lot.width);
    const d = dist.get(current)!;

    for (const [dx, dy] of NEIGHBORS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!isNavigable(lot, nx, ny)) continue;
      const ni = idx(lot.width, nx, ny);
      if (dist.has(ni)) continue;
      dist.set(ni, d + 1);
      prev.set(ni, current);
      queue.push(ni);
    }
  }

  return { dist, prev };
}

/** Reconstruct the path of tile indices from a BFS `prev` map to `target`. */
export function reconstruct(prev: Map<number, number>, target: number): number[] {
  const path: number[] = [target];
  let cur = target;
  while (prev.has(cur)) {
    cur = prev.get(cur)!;
    path.push(cur);
  }
  path.reverse();
  return path;
}

/** The navigable tiles orthogonally adjacent to a given tile index. */
export function navigableNeighborsOf(lot: ParkingLot, tileIndex: number): number[] {
  const x = tileIndex % lot.width;
  const y = Math.floor(tileIndex / lot.width);
  const result: number[] = [];
  for (const [dx, dy] of NEIGHBORS) {
    const nx = x + dx;
    const ny = y + dy;
    if (isNavigable(lot, nx, ny)) result.push(idx(lot.width, nx, ny));
  }
  return result;
}

export function tileToPoint(
  lot: ParkingLot,
  tileIndex: number
): { x: number; y: number } {
  return { x: tileIndex % lot.width, y: Math.floor(tileIndex / lot.width) };
}
